import React, { useEffect, useState } from 'react';
import moment from 'moment';

import './League.css';

import api from '../utils/api';

import underwhelmingGentsUtil from '../lib/underwhelmingGentsUtil';
import constants from '../lib/constants';
import Loader from '../lib/components/Loader';
import OverlayLoader from '../lib/components/OverlayLoader';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Player from '../Player/Player';
import futureDraftData from '../assets/futureDraftData';
import CurrentSeasonTab from './CurrentSeasonTab';

const Roster = ({ roster: initialRoster, canUpdate }) => {
    const [roster, setRoster] = useState(initialRoster);
    const [contractsToUpdate, setContractsToUpdate] = useState({});
    const [dirtyData, setDirtyData] = useState({});
    const [salaryTotal, setSalaryTotal] = useState(0);
    const [showLoaderOverlay, setShowLoaderOverlay] = useState(false);

    useEffect(() => {
        setRoster(initialRoster);
    }, [initialRoster]);

    useEffect(() => {
        setSalaryTotal(
            roster.map(player => (player.injured ? 0 : player.contract === 0 ? 1 : player.contract)).reduce((a, b) => a + b, 0)
        );
    }, [roster]);

    const handleContractUpdate = (id, contract) => {
        const updatedContracts = { ...contractsToUpdate, [id]: contract };
        const updatedDirtyData = { ...dirtyData, [id]: true };
        const updatedRoster = roster.map(player =>
            player.id === id ? { ...player, contract: Number(contract) } : player
        );

        setContractsToUpdate(updatedContracts);
        setDirtyData(updatedDirtyData);
        setRoster(updatedRoster);
        setShowLoaderOverlay(true);

        api.post('/api/v2/players/contract/update', { id: id, contract: Number(contract) })
            .then(() => {
                setContractsToUpdate({});
                setDirtyData({});
                setShowLoaderOverlay(false);
                window.alert('Contract updated successfully');
            })
            .catch(error => {
                setShowLoaderOverlay(false);
                console.error({ error });
                const message = error?.response?.data?.message;
                window.alert(`Could not update contract - ${message || 'Unknown error'}`);
            });
    };

    return (
        <div id="rosterContainer" className="rosterContainer">
            {showLoaderOverlay && <OverlayLoader/>}
            <div>
                {roster.map((player, index) => (
                    <Player
                        key={player.id}
                        playerData={player}
                        index={index}
                        dirtyData={dirtyData[player.id]}
                        handleContractUpdate={handleContractUpdate}
                        canUpdate={canUpdate}
                    />
                ))}
            </div>
            <div id="salaryTotal" className="mt-3">
                <div className={salaryTotal > 34 ? 'alert alert-danger text-center' : 'alert alert-dark text-center'}>
                    <div>Current Salary Total: <strong>{salaryTotal}</strong></div>
                    <div>({roster.filter(p => p.contract === 0).length} players without contracts)</div>
                </div>
            </div>
        </div>
    );
};

const RosterProjectionModal = ({ year, managerName, projection, draftOrders }) => {
    const modalId = `rosterProjectionModal-${year}`;
    const eligiblePlayers = projection.taggedSave
        ? projection.returning.concat([projection.taggedSave])
        : projection.returning;
    const { starters, bench } = underwhelmingGentsUtil.getStartingLineupAndBench(eligiblePlayers);

    return (
        <div className="modal fade" id={modalId} tabIndex="-1" aria-hidden="true">
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
                <div className="modal-content bg-dark text-white border-secondary">
                    <div className="modal-header border-secondary">
                        <h5 className="modal-title">{managerName}'s Projected Roster After {year} Draft</h5>
                        <button type="button" className="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <p><strong>Projected Roster Size: {projection.rosterSize}</strong></p>

                        {projection.taggedSave && (
                            <div className="mb-3">
                                <h6>Assumed Franchise Tag Save</h6>
                                <div className="text-muted small mb-2">Assumes the manager tags their best expiring player instead of losing them.</div>
                                <span className="badge bg-info text-dark">
                                    <i className="bi bi-star-fill"></i> {projection.taggedSave.name}
                                </span>
                            </div>
                        )}

                        <div className="mb-3">
                            <h6>Projected Starting Roster</h6>
                            <table className="table table-dark table-striped table-bordered caption-top">
                                <thead>
                                    <tr>
                                        <th>Pos</th>
                                        <th>Player</th>
                                        <th>Y!</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {starters.map((slot, i) => (
                                        <tr key={slot.label + i}>
                                            <td>{slot.label}</td>
                                            <td>{slot.player ? slot.player.name : '-'}</td>
                                            <td>{slot.player ? (slot.player.yahooleaguerank || '-') : '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="mb-3">
                            <h6>Bench ({bench.length + projection.hypotheticalPicks.length})</h6>
                            {bench.length === 0 && projection.hypotheticalPicks.length === 0 ? (
                                <div className="text-muted small">None</div>
                            ) : (
                                <ul className="list-group list-group-dark">
                                    {bench.map(player => (
                                        <li key={player.id} className="list-group-item list-group-item-dark">{player.posname} - {player.name}</li>
                                    ))}
                                    {projection.hypotheticalPicks.map((pick, i) => {
                                        const pickDraftOrder = draftOrders[pick.year]?.order;
                                        const pickIsProjected = draftOrders[pick.year]?.isProjected;
                                        const pickPosition = pickDraftOrder ? pickDraftOrder.indexOf(pick.originalOwner) + 1 + (pick.round - 1) * 12 : null;
                                        return (
                                            <li key={'draftPick' + i} className="list-group-item list-group-item-dark text-muted">
                                                Draft Pick - {pick.year} {underwhelmingGentsUtil.getOrdinal(pick.round)} Round
                                                {pickPosition ? ` (${pickIsProjected ? 'projected ' : ''}pick #${pickPosition})` : ''}
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>

                        <div className="mb-3">
                            <h6>Departing Players ({projection.departing.length})</h6>
                            {projection.departing.length === 0 ? (
                                <div className="text-muted small">None</div>
                            ) : (
                                <ul className="list-group list-group-dark">
                                    {projection.departing.map(player => (
                                        <li key={player.id} className="list-group-item list-group-item-dark">{player.name}</li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <div>
                            <h6>Draft Picks Added</h6>
                            <div className="text-muted small">{projection.picksAddedThisYear} picks in {year} ({projection.hypotheticalPicks.length} total added through {year})</div>
                        </div>
                    </div>
                    <div className="modal-footer border-secondary">
                        <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const TeamFutureData = ({ managerFutureDraftPicks, managerName, roster }) => {
    const [draftOrders, setDraftOrders] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get('/api/v1/draft/orders')
            .then(({ data }) => {
                setDraftOrders(data);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching draft orders:', error);
                setLoading(false);
            });
    }, []);

    const managerDraftData = managerFutureDraftPicks;
    const pickCounts = underwhelmingGentsUtil.getPickCountsByYear(managerDraftData);
    const projections = underwhelmingGentsUtil.getRosterProjections(roster, managerDraftData);

    if (loading) {
        return <div>Loading draft data...</div>;
    }

    return (
        <div className="row">
            {Object.keys(managerDraftData).map(year => {
                const draftOrderData = draftOrders[year];
                const draftOrder = draftOrderData?.order;
                const isProjected = draftOrderData?.isProjected;
                const projection = projections[year];
                const collapseId = `pickDetail-${year}`;

                return (
                    <div key={year + 'draft'} className="col-md-6 col-xl-3 mb-3">
                        <div className="card bg-dark text-white h-100">
                            <div className="bg-dark card-header">
                                <h3 className="mb-0">{year} Draft</h3>
                            </div>
                            <div className="card-body">
                                <div>
                                    <small className="text-muted">FAAB: ${futureDraftData[year]?.faab[managerName] || 100}</small>
                                </div>
                                <div>
                                    <small className="text-muted">Picks: {pickCounts[year]} ({underwhelmingGentsUtil.getPickRoundSummary(managerDraftData[year])})</small>
                                </div>
                                <div className="mb-2">
                                    <small className="text-muted">Projected Roster Size: {projection.rosterSize}</small>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-info mb-2 me-2"
                                    data-bs-toggle="modal"
                                    data-bs-target={`#rosterProjectionModal-${year}`}
                                >
                                    View Roster Projection
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary mb-2"
                                    data-bs-toggle="collapse"
                                    data-bs-target={`#${collapseId}`}
                                    aria-expanded="true"
                                    aria-controls={collapseId}
                                >
                                    Show/Hide Pick Details
                                </button>
                                <div className="collapse show" id={collapseId}>
                                    <div>
                                        <small className="text-muted">Original Owner, {isProjected ? '(projected pick #)' : '(pick #)'}</small>
                                    </div>
                                    <div className="row mt-2">
                                        {managerDraftData[year].slice(0, 3).map((round, roundIndex) => (
                                            <div key={roundIndex} className="col-4">
                                                <strong>{'Round ' + (roundIndex + 1)}</strong>
                                                {round.map(pick => {
                                                    const pickPosition = draftOrder?.indexOf(pick) + 1 + roundIndex * 12;
                                                    return (<div key={pick + pickPosition}>{pick + ' (' + pickPosition + ')'}</div>);
                                                })}
                                            </div>
                                        ))}
                                    </div>
                                    <div className="row mt-2">
                                        {managerDraftData[year].slice(3).map((round, i) => {
                                            const roundIndex = i + 3;
                                            return (
                                                <div key={roundIndex} className="col-3">
                                                    <strong>{'Round ' + (roundIndex + 1)}</strong>
                                                    {round.map(pick => {
                                                        const pickPosition = draftOrder?.indexOf(pick) + 1 + roundIndex * 12;
                                                        return (<div key={pick + pickPosition}>{pick + ' (' + pickPosition + ')'}</div>);
                                                    })}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <RosterProjectionModal year={year} managerName={managerName} projection={projection} draftOrders={draftOrders} />
                    </div>
                )
            })}
        </div>
    );
};

const RosterTab = ({ managerName, playerData, outgoingPicks, canUpdate }) => {
    const roster = playerData.filter(player => player.manager === managerName);
    const managerFutureDraftPicks = underwhelmingGentsUtil.getAllPicks(outgoingPicks, managerName);

    return (
        <div>
            <div id="roster">
                <h3>Roster</h3>
                <Roster
                    roster={roster.sort((e1, e2) => e1.pos - e2.pos)}
                    canUpdate={canUpdate}
                />
            </div>
            <div id="teamFutures" style={{marginTop: 24}}>
                <h3>Future Draft Picks</h3>
                <TeamFutureData
                    managerFutureDraftPicks={managerFutureDraftPicks}
                    managerName={managerName}
                    roster={roster}
                />
            </div>
        </div>
    );
};

const ManagerHistoryTab = ({ managerName }) => {
    const [rosterMoves, setRosterMoves] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        setError(null);
        api.get(`/api/v1/managers/${managerName}/rosterMoveHistory`)
            .then(({ data }) => {
                setRosterMoves(data || []);
            })
            .catch((err) => {
                console.error('Error fetching manager roster move history:', err);
                setError('Failed to load roster move history');
            })
            .finally(() => {
                setLoading(false);
            });
    }, [managerName]);

    if (loading) {
        return <Loader />;
    }

    if (error) {
        return <div className="alert alert-danger">{error}</div>;
    }

    if (!rosterMoves || rosterMoves.length === 0) {
        return <div className="alert alert-dark">No roster move history found.</div>;
    }

    // An add/drop pair shares a transaction_key - group so it renders as one card
    const transactionsByKey = rosterMoves.reduce((acc, move) => {
        if (!acc[move.transaction_key]) {
            acc[move.transaction_key] = [];
        }
        acc[move.transaction_key].push(move);
        return acc;
    }, {});

    const sortedTransactions = Object.values(transactionsByKey)
        .sort((a, b) => Number(b[0].timestamp) - Number(a[0].timestamp));

    return (
        <div>
            {sortedTransactions.map((moves) => {
                const faabBid = moves.find(move => move.action === 'add' && move.faab_bid != null)?.faab_bid;
                return (
                    <div key={moves[0].transaction_key} className="card bg-secondary text-white mb-2">
                        <div className="card-body p-2">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                                <span className="badge bg-warning text-dark">Roster Move</span>
                                <small className="text-muted">
                                    {moment(Number(moves[0].timestamp)).format('MMM D, YYYY')}
                                </small>
                            </div>
                            <ul className="mb-0 ps-3" style={{ fontSize: '0.875rem' }}>
                                {moves.map((move, i) => (
                                    <li key={i}>
                                        {move.action === 'add' ? 'Added' : 'Dropped'}:{' '}
                                        <Link to={`/player/${move.player_id}`} className="text-white">
                                            {move.player_name}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                            {faabBid != null && (
                                <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                    FAAB: ${faabBid}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const Leaguev2 = ({ userData, playerData, outgoingPicks }) => {
    const { managerName: managerNameParam } = useParams();
    const navigate = useNavigate();
    
    const teams = constants.managerList;
    const managerParamIndex = teams.findIndex((manager) => manager === managerNameParam);
    const [selectedManager, setSelectedManager] = useState(
        managerParamIndex > -1 ? managerNameParam : teams[0]
    );
    const [activeTab, setActiveTab] = useState('roster');
    const [teamDetails, updateTeamDetails] = useState({});
    const [isTeamDetailsLoading, updateIsTeamDetailsLoading] = useState(true);

    useEffect(() => {
        updateIsTeamDetailsLoading(true);
        api.get('/api/v1/managers').then(({ data }) => {
            updateTeamDetails(data);
            updateIsTeamDetailsLoading(false);
        }).catch(err => {
            console.error('Error retrieving team details', err);
            updateTeamDetails({});
            updateIsTeamDetailsLoading(false);
        });
    }, []);

    useEffect(() => {
        if (managerNameParam && teams.includes(managerNameParam)) {
            setSelectedManager(managerNameParam);
        }
    }, [managerNameParam]);

    const handleManagerChange = (e) => {
        const newManager = e.target.value;
        setSelectedManager(newManager);
        navigate(`/league/${newManager}`);
    };

    const canUpdate = userData?.permissions === 'admin' || userData?.permissions === selectedManager;

    return (
        <div id="leagueContainer">
            <h2>League</h2>
            
            {/* Manager Dropdown */}
            <div className="mb-4">
                <label htmlFor="managerSelect" className="form-label fw-bold">Select Manager:</label>
                <select 
                    id="managerSelect"
                    className="form-select form-select-lg bg-dark text-white border-secondary" 
                    value={selectedManager}
                    onChange={handleManagerChange}
                >
                    {teams.map(team => (
                        <option key={team} value={team}>
                            {team}
                        </option>
                    ))}
                </select>
            </div>

            {/* Tabs */}
            <ul className="nav nav-tabs nav-tabs-dark mb-3">
                <li className="nav-item">
                    <button 
                        className={`nav-link ${activeTab === 'roster' ? 'active' : ''}`}
                        onClick={() => setActiveTab('roster')}
                    >
                        Roster
                    </button>
                </li>
                <li className="nav-item">
                    <button 
                        className={`nav-link ${activeTab === 'current-season' ? 'active' : ''}`}
                        onClick={() => setActiveTab('current-season')}
                    >
                        Current Season
                    </button>
                </li>
                <li className="nav-item">
                    <button 
                        className={`nav-link ${activeTab === 'history' ? 'active' : ''}`}
                        onClick={() => setActiveTab('history')}
                    >
                        History
                    </button>
                </li>
            </ul>

            {/* Tab Content */}
            <div className="tab-content">
                {isTeamDetailsLoading ? (
                    <Loader />
                ) : (
                    <>
                        {activeTab === 'roster' && (
                            <RosterTab 
                                managerName={selectedManager}
                                playerData={playerData}
                                outgoingPicks={outgoingPicks}
                                canUpdate={canUpdate}
                            />
                        )}
                        {activeTab === 'current-season' && (
                            <CurrentSeasonTab managerName={selectedManager} />
                        )}
                        {activeTab === 'history' && (
                            <ManagerHistoryTab managerName={selectedManager} />
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default Leaguev2;
